// API Configuration and Base Client
const isBrowser = (): boolean => typeof window !== 'undefined';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);
const LOCAL_ENV_HOSTNAMES = new Set([...LOCAL_HOSTNAMES, 'host.docker.internal']);

const env = import.meta.env as unknown as Record<string, string | undefined>;

type ServiceKey =
  | 'retail'
  | 'auth'
  | 'seller'
  | 'admin'
  | 'delivery'
  | 'notifications'
  | 'documents'
  | 'workflow'
  | 'returns';

/**
 * KAN-2042 — THE SINGLE GATEWAY CONTRACT. Read this before adding a service.
 *
 * The backend does NOT publish one DNS host per microservice. It runs ONE nginx
 * gateway that PATH-routes every service behind a single origin (see
 * `RetailWebsiteBackendOnly/nginx/nginx.conf` for the service<->port map and the
 * `server.proxy` block in this repo's `vite.config.js` for the same map expressed
 * as paths). The frontend therefore has exactly ONE API origin.
 *
 * WHAT THIS REPLACED, AND WHY IT WAS A LIVE OUTAGE: this module used to synthesise
 * NINE per-service hostnames (`auth-api.<root>`, `seller-api.<root>`, ...) from the
 * portal's own hostname. Those hosts were a `thejalwave.com`-era artefact and were
 * NEVER created under `retail.dhruvantasystems.com` — all nine were NXDOMAIN in
 * production, so every data call failed DNS resolution while the HTML shell served
 * a healthy 200. Collapsing to one gateway origin is what makes the app functional.
 *
 * The prefixes below are the FULL path each service is mounted at on the gateway.
 * Call sites append the rest (e.g. `sellerApiClient.get('/v2/sellers/123')` becomes
 * `<gateway>/api/v2/sellers/123`), which is why most services pin the bare `/api`
 * mount while `auth` and `notifications` carry their service segment here — that is
 * the existing, unchanged call-site convention. `tests/apiBaseResolution.test.ts`
 * asserts every one of these against the backend map; update BOTH together.
 */
const SERVICE_PATH_PREFIXES: Record<ServiceKey, string> = {
  // retail-core-service — /api/v2/{products,categories,cart,orders,wishlist,
  // addresses,print-orders,analytics,payments}. Call sites supply the `/v2/...` tail,
  // so the mount is the bare `/api`.
  retail: '/api',
  // auth-service — /api/v2/auth/** (login, otp, password, privacy). Call sites
  // supply `/login`, `/privacy/consents`, ... so the service segment lives here.
  auth: '/api/v2/auth',
  // seller-service — /api/v2/sellers/**. Call sites supply `/v2/sellers/...`.
  seller: '/api',
  // admin-service — /api/v2/admin/**. Call sites supply `/v2/admin/...`.
  admin: '/api',
  // delivery-service — /api/v2/delivery/**. Call sites supply `/v2/delivery/...`.
  delivery: '/api',
  // retail-core-service NotificationController — /api/v2/notifications/{history,
  // preferences}. NOTE: notification-service separately owns the UNVERSIONED-`/api`
  // mount `/v2/notifications/**` (send, templates, health). The two are different
  // services with overlapping names; this client targets the retail-core mount,
  // which is the one the customer-facing preference screens use. The `/templates`
  // and `/in-app/*` calls made against this client are on the WRONG mount / have no
  // backend at all — tracked in KAN-2080, deliberately not papered over here.
  notifications: '/api/v2/notifications',
  // document-service — mounted at `/v2/documents/**` with NO `/api` prefix.
  documents: '/v2/documents',
  // workflow-service — mounted at `/v2/workflows/**` with NO `/api` prefix.
  workflow: '/v2/workflows',
  // return-service — /api/v2/returns/requests (CustomerReturnController). The same
  // service ALSO exposes `/v2/returns/**` and `/v2/ops/returns` without the `/api`
  // prefix; call sites supply `/v2/returns/requests`, so the mount is bare `/api`.
  returns: '/api',
};

/**
 * The DNS label the single gateway is published under, relative to the hosted root
 * resolved by `extractHostedRootDomain`. This is the ONLY hostname fragment this
 * module synthesises, and it is a last-resort default — every environment can point
 * somewhere else with `VITE_API_GATEWAY_URL` (absolute origin, or a leading-slash
 * same-origin path if the deployment fronts the gateway with a reverse proxy).
 */
const GATEWAY_HOST_LABEL = 'api';

const isLocalHostname = (hostname?: string | null): boolean => {
  if (!hostname) {
    return false;
  }
  return LOCAL_ENV_HOSTNAMES.has(hostname);
};

const isLikelyLocalUrl = (candidate?: string): boolean => {
  if (!candidate) {
    return false;
  }
  try {
    const parsed = new URL(candidate);
    return isLocalHostname(parsed.hostname);
  } catch {
    return false;
  }
};

const rewriteHostDockerInternal = (url: string): string => {
  if (!isBrowser()) {
    return url;
  }
  if (!url.includes('host.docker.internal')) {
    return url;
  }

  const locationHost = window.location.hostname || 'localhost';
  if (!LOCAL_HOSTNAMES.has(locationHost)) {
    return url;
  }

  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'host.docker.internal') {
      return url;
    }

    parsed.hostname = locationHost;
    // Swap the HOST only. This runs on a gateway ORIGIN, so it must not append or
    // normalise any path — the per-service prefix is concatenated afterwards, and
    // adding an `/api` here would yield `/api/api/v2/...`.
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return url;
  }
};

const isIpAddressHostname = (hostname: string): boolean =>
  /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) || hostname.includes(':');

/**
 * KAN-2031 (Dhruvanta Retail rebrand) — the hosted root for every retail portal
 * and every retail API host.
 *
 * The generic rule below ("last two labels") assumed a two-label brand root
 * (`<portal>.<brand>.com` -> `<brand>.com`), which is what the pre-KAN-2031
 * domains were. Dhruvanta Retail lives one level deeper: the storefront is
 * `retail.dhruvantasystems.com` and the other portals are
 * `<portal>.retail.dhruvantasystems.com`. Falling back to the
 * generic rule would resolve APIs onto the SHARED platform apex
 * (`auth-api.dhruvantasystems.com`), which is the wrong namespace — those hosts
 * belong to the retail family. Anything at or under this root therefore pins to
 * this root, preserving the pre-rebrand behaviour (portal host -> sibling API
 * host in the same family).
 */
const RETAIL_ROOT_DOMAIN = 'retail.dhruvantasystems.com';

const extractHostedRootDomain = (hostnameOrDomain: string): string | undefined => {
  const normalized = hostnameOrDomain.trim().toLowerCase().replace(/\.+$/, '');
  if (!normalized || LOCAL_ENV_HOSTNAMES.has(normalized) || isIpAddressHostname(normalized)) {
    return undefined;
  }

  if (normalized === RETAIL_ROOT_DOMAIN || normalized.endsWith(`.${RETAIL_ROOT_DOMAIN}`)) {
    return RETAIL_ROOT_DOMAIN;
  }

  const segments = normalized.split('.').filter(Boolean);
  if (segments.length < 2) {
    return undefined;
  }

  return segments.slice(-2).join('.');
};

/**
 * The single gateway HOST for a portal hostname — `api.<hosted root>`.
 *
 * Service-independent BY DESIGN: one gateway serves every service, and the service
 * is selected by PATH, never by hostname. Returns undefined for local/IP access so
 * dev falls through to the same-origin Vite proxy.
 */
const resolveGatewayHost = (hostnameOrDomain: string): string | undefined => {
  const rootDomain = extractHostedRootDomain(hostnameOrDomain);
  if (!rootDomain) {
    return undefined;
  }

  return `${GATEWAY_HOST_LABEL}.${rootDomain}`;
};

/** The single gateway ORIGIN (scheme + host, no path) for a portal hostname. */
const resolveHostedGatewayOrigin = (
  hostname: string,
  protocol: string
): string | undefined => {
  if (LOCAL_HOSTNAMES.has(hostname)) {
    return undefined;
  }

  const gatewayHost = resolveGatewayHost(hostname);
  if (!gatewayHost) {
    return undefined;
  }

  return `${protocol}//${gatewayHost}`;
};

/**
 * The full base URL for one service: the single gateway origin plus that service's
 * path prefix. This is the function the regression test drives.
 */
const resolveHostedApiBaseForHost = (
  hostname: string,
  protocol: string,
  service: ServiceKey
): string | undefined => {
  const origin = resolveHostedGatewayOrigin(hostname, protocol);
  if (!origin) {
    return undefined;
  }

  return `${origin}${SERVICE_PATH_PREFIXES[service]}`;
};

/**
 * Normalise a configured gateway ORIGIN. Unlike `normalizeBaseUrl` this must NOT
 * force an `/api` suffix — the per-service prefix supplies that, and doubling it
 * would produce `/api/api/v2/...`.
 *
 * Accepts an absolute origin (`https://api.example.com`), a protocol-relative
 * origin (`//api.example.com`), or the empty string / a leading-slash path for a
 * same-origin deployment fronted by a reverse proxy.
 */
const normalizeGatewayOrigin = (candidate?: string): string | undefined => {
  if (typeof candidate !== 'string') {
    return undefined;
  }

  // Strip trailing slashes, then a trailing `/api`. THE `/api` STRIP IS LOAD-BEARING:
  // the documented convention for the legacy `VITE_API_BASE_URL` (and every value in
  // `.env.template`) ends in `/api`, e.g. `http://localhost:18082/api`. Because the
  // per-service prefix below already starts with `/api`, keeping it would build
  // `http://host/api/api/v2/auth/login` and 404 every single call. Strip repeatedly
  // so `.../api/api` normalises too.
  let trimmed = candidate.trim().replace(/\/+$/, '');
  while (/\/api$/i.test(trimmed)) {
    trimmed = trimmed.replace(/\/api$/i, '').replace(/\/+$/, '');
  }

  if (trimmed.length === 0) {
    return undefined;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    // A bare `https://host` with nothing left after the strip is a valid origin.
    return trimmed;
  }

  if (trimmed.startsWith('//')) {
    if (!isBrowser()) {
      return undefined;
    }
    return `${window.location.protocol}${trimmed}`;
  }

  // A leading-slash path means "same origin, behind this prefix".
  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  return undefined;
};

/**
 * THE ONE API ORIGIN every service call goes through (KAN-2042).
 *
 * Resolution order, highest priority first:
 *   1. `VITE_API_GATEWAY_URL` — the canonical, explicit, per-environment knob.
 *   2. `VITE_API_BASE_URL`    — the legacy name, honoured so existing deployments
 *                               and `.env` files keep working.
 *   3. Derived `api.<hosted root>` when the app is served from a real domain.
 *   4. Same-origin ('') — local dev and SSR. `vite.config.js` proxies `/api/v2/*`
 *      to the right service port, so dev needs no host at all and incurs no CORS.
 *
 * An explicitly LOCAL env value is ignored in a HOSTED context: that combination
 * only ever happens when a `.env` meant for a laptop leaks into a deployed build,
 * and honouring it would point production at `localhost`.
 */
const resolveGatewayOrigin = (): string => {
  const hostedContext = isBrowser() && !LOCAL_HOSTNAMES.has(window.location.hostname);

  const configured =
    normalizeGatewayOrigin(env.VITE_API_GATEWAY_URL) ??
    normalizeGatewayOrigin(env.VITE_API_BASE_URL);

  if (configured) {
    if (configured.startsWith('/')) {
      return configured;
    }
    if (!(hostedContext && isLikelyLocalUrl(configured))) {
      return rewriteHostDockerInternal(configured);
    }
  }

  if (isBrowser()) {
    const { hostname, protocol } = window.location;
    const derived = resolveHostedGatewayOrigin(hostname, protocol);
    if (derived) {
      return derived;
    }
  }

  // Same-origin: the Vite dev proxy (and any reverse proxy in front of a preview
  // build) path-routes `/api/v2/*` to the correct backend service.
  return '';
};

const GATEWAY_ORIGIN = resolveGatewayOrigin();

const resolveServiceBase = (service: ServiceKey): string =>
  `${GATEWAY_ORIGIN}${SERVICE_PATH_PREFIXES[service]}`;

const SESSION_REFRESH_ENDPOINT = '/refresh';
let refreshSessionPromise: Promise<boolean> | null = null;

// Login route for the SPA. Hard auth failures (401 that survives a refresh on a request that
// requires auth) bounce the user here instead of silently failing. Guarded so we only ever
// redirect once and never from the login page itself (no redirect loop).
const LOGIN_PATH = '/login';
let redirectingToLogin = false;

const redirectToLogin = (): void => {
  if (!isBrowser() || redirectingToLogin) {
    return;
  }
  const { pathname, search, hash } = window.location;
  // Already on (or heading to) the login page — nothing to do.
  if (pathname === LOGIN_PATH) {
    return;
  }
  redirectingToLogin = true;
  const next = encodeURIComponent(`${pathname}${search}${hash}`);
  window.location.assign(`${LOGIN_PATH}?next=${next}`);
};

export const __apiResolution = {
  extractHostedRootDomain,
  resolveGatewayHost,
  resolveHostedGatewayOrigin,
  resolveHostedApiBaseForHost,
  normalizeGatewayOrigin,
  SERVICE_PATH_PREFIXES,
};

/**
 * THE 401 vs 403 CONTRACT (KAN-2036). Read this before touching any auth-failure
 * branch in this file.
 *
 *   401 UNAUTHENTICATED — there is no valid session (or it expired). The client may
 *       attempt ONE silent refresh and, if that fails, bounce the user to /login.
 *
 *   403 FORBIDDEN — the caller IS authenticated but is NOT authorised for this
 *       resource (wrong role, wrong portal, insufficient scope). It MUST be
 *       surfaced INLINE by the calling screen. It must NEVER refresh, NEVER
 *       redirect to /login, and NEVER clear the session.
 *
 * WHY THIS IS SPELLED OUT: conflating the two is what destroyed valid production
 * sessions on PrintAnywhere — a 403 from one under-privileged widget signed the
 * user out of the whole app. `request()` and `download()` below therefore key their
 * recovery path on `response.status === 401` ONLY; 403 falls through to the normal
 * error path with `status` attached, which is what these helpers read.
 */
export type ApiErrorLike = Error & { status?: number; details?: unknown };

/** The HTTP status carried by a thrown API error, or undefined for a non-HTTP failure. */
export const statusOf = (error: unknown): number | undefined =>
  error instanceof Error ? (error as ApiErrorLike).status : undefined;

/** True for "not signed in" — the ONLY class that may bounce the user to sign-in. */
export const isUnauthenticatedError = (error: unknown): boolean => statusOf(error) === 401;

/**
 * True for "signed in, but not allowed here". Callers MUST render this inline and
 * MUST NOT sign the user out. See the contract note above.
 */
export const isForbiddenError = (error: unknown): boolean => statusOf(error) === 403;

/**
 * Every service base is `<the one gateway origin> + <that service's path prefix>`.
 * There is exactly ONE origin here — that invariant is the whole point of KAN-2042
 * and `tests/apiBaseResolution.test.ts` asserts it.
 */
export const serviceBaseUrls: Record<ServiceKey, string> = {
  retail: resolveServiceBase('retail'),
  auth: resolveServiceBase('auth'),
  seller: resolveServiceBase('seller'),
  admin: resolveServiceBase('admin'),
  delivery: resolveServiceBase('delivery'),
  notifications: resolveServiceBase('notifications'),
  documents: resolveServiceBase('documents'),
  workflow: resolveServiceBase('workflow'),
  returns: resolveServiceBase('returns'),
};

/** The single API origin in force for this build. Exported for diagnostics/tests. */
export const apiGatewayOrigin = GATEWAY_ORIGIN;

if (isBrowser()) {
  (window as typeof window & { __SERVICE_BASE_URLS__?: typeof serviceBaseUrls }).__SERVICE_BASE_URLS__ =
    serviceBaseUrls;
}

interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: ApiErrorBody;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(includeAuth: boolean = false): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };

    // Only include the X-Portal-Key header for authenticated requests to avoid CORS issues on public endpoints.
    if (includeAuth) {
      const portal = document?.body?.dataset?.portal;
      if (portal) {
        headers['X-Portal-Key'] = portal;
      }
    }

    return headers;
  }

  private isAuthRefreshRequest(url: string): boolean {
    return url === `${serviceBaseUrls.auth}${SESSION_REFRESH_ENDPOINT}` || url.endsWith('/api/v2/auth/refresh');
  }

  private async refreshSession(): Promise<boolean> {
    if (refreshSessionPromise) {
      return refreshSessionPromise;
    }

    refreshSessionPromise = (async () => {
      try {
        const response = await fetch(`${serviceBaseUrls.auth}${SESSION_REFRESH_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: '{}',
          credentials: 'include',
        });

        return response.ok;
      } catch {
        return false;
      } finally {
        refreshSessionPromise = null;
      }
    })();

    return refreshSessionPromise;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requiresAuth: boolean = false,
    retryOnAuthFailure: boolean = true,
    // When true (default), a hard 401 on an authed request bounces the user to /login. Auth
    // *probe* calls (e.g. the silent "am I logged in?" check on app boot) pass false so an
    // anonymous visitor browsing the public storefront is never kicked to the login page.
    redirectOnAuthFailure: boolean = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(requiresAuth);

    const bodyIsFormData =
      typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (bodyIsFormData) {
      delete headers['Content-Type'];
    }

    try {
      const response = await fetch(url, {
        ...options,
        credentials: options.credentials ?? 'include',
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      // 401 ONLY — see the 401 vs 403 contract above. A 403 deliberately does NOT
      // enter this branch: it is an authorisation failure on a LIVE session and is
      // surfaced inline by the caller.
      if (
        response.status === 401 &&
        requiresAuth &&
        !this.isAuthRefreshRequest(url)
      ) {
        if (retryOnAuthFailure) {
          const refreshed = await this.refreshSession();
          if (refreshed) {
            return this.request<T>(endpoint, options, requiresAuth, false, redirectOnAuthFailure);
          }
        }
        // Refresh didn't recover the session (or this was already the post-refresh retry):
        // the session is genuinely gone — bounce to login instead of failing silently.
        if (redirectOnAuthFailure) {
          redirectToLogin();
        }
      }

      // Some endpoints may legitimately return 204 No Content
      if (response.status === 204) {
        return undefined as T;
      }

      const contentType = response.headers.get('content-type') ?? '';
      const isJson = contentType.includes('application/json');
      const rawBody = isJson ? await response.text() : '';
      let parsedBody: unknown = undefined;

      if (isJson && rawBody) {
        try {
          parsedBody = JSON.parse(rawBody);
        } catch {
          parsedBody = undefined;
        }
      }

      if (!response.ok) {
        const errorMessage = this.resolveErrorMessage(parsedBody, rawBody, response.status, endpoint);
        const error = new Error(errorMessage);
        (error as Error & { details?: unknown; status?: number }).details = this.resolveErrorDetails(parsedBody);
        (error as Error & { details?: unknown; status?: number }).status = response.status;
        throw error;
      }

      if (!isJson) {
        return undefined as T;
      }

      if (this.isEnvelope<T>(parsedBody)) {
        if (!parsedBody.success) {
          const error = new Error(parsedBody.error?.message || parsedBody.error?.code || 'Request failed');
          (error as Error & { details?: unknown }).details = parsedBody.error?.details;
          throw error;
        }
        return parsedBody.data as T;
      }

      if (parsedBody === undefined) {
        return undefined as T;
      }

      return parsedBody as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async download(
    endpoint: string,
    requiresAuth: boolean = false,
    accept: string = 'application/octet-stream',
    retryOnAuthFailure: boolean = true
  ): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(requiresAuth);
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...headers,
        Accept: accept
      }
    });

    if (
      response.status === 401 &&
      requiresAuth &&
      !this.isAuthRefreshRequest(url)
    ) {
      if (retryOnAuthFailure) {
        const refreshed = await this.refreshSession();
        if (refreshed) {
          return this.download(endpoint, requiresAuth, accept, false);
        }
      }
      // A failed authed download means the session is gone — bounce to login.
      redirectToLogin();
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type') ?? '';
      const isJson = contentType.includes('application/json');
      const rawBody = isJson ? await response.text() : await response.text().catch(() => '');
      let parsedBody: unknown = undefined;
      if (isJson && rawBody) {
        try {
          parsedBody = JSON.parse(rawBody);
        } catch {
          parsedBody = undefined;
        }
      }
      const errorMessage = this.resolveErrorMessage(parsedBody, rawBody, response.status, endpoint);
      const error = new Error(errorMessage);
      (error as Error & { details?: unknown; status?: number }).details = this.resolveErrorDetails(parsedBody);
      (error as Error & { details?: unknown; status?: number }).status = response.status;
      throw error;
    }

    return response.blob();
  }

  private isEnvelope<T>(payload: unknown): payload is ApiEnvelope<T> {
    return typeof payload === 'object' && payload !== null && 'success' in payload;
  }

  private resolveErrorDetails(payload: unknown): unknown {
    if (this.isEnvelope(payload)) {
      return payload.error?.details;
    }
    if (payload && typeof payload === 'object' && 'details' in payload) {
      return (payload as { details?: unknown }).details;
    }
    return undefined;
  }

  private resolveErrorMessage(payload: unknown, rawBody: string, status: number, endpoint: string): string {
    if (this.isEnvelope(payload)) {
      return payload?.error?.message || payload?.error?.code || `Request to ${endpoint} failed with status ${status}`;
    }
    if (payload && typeof payload === 'object') {
      const objectPayload = payload as Record<string, unknown>;
      const messageCandidate =
        (typeof objectPayload.message === 'string' && objectPayload.message) ||
        (typeof objectPayload.error === 'string' && objectPayload.error);
      if (messageCandidate) {
        return messageCandidate;
      }
    }
    if (rawBody) {
      return rawBody;
    }
    return `Request to ${endpoint} failed with status ${status}`;
  }

  async get<T>(
    endpoint: string,
    requiresAuth: boolean = false,
    opts: { redirectOnAuthFailure?: boolean } = {}
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      { method: 'GET' },
      requiresAuth,
      true,
      opts.redirectOnAuthFailure ?? true
    );
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    requiresAuth: boolean = false
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      requiresAuth
    );
  }

  async postForm<T>(
    endpoint: string,
    formData: FormData,
    requiresAuth: boolean = false
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: formData,
      },
      requiresAuth
    );
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    requiresAuth: boolean = false
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
      requiresAuth
    );
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    requiresAuth: boolean = false
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
      requiresAuth
    );
  }

  async delete<T>(endpoint: string, requiresAuth: boolean = false): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, requiresAuth);
  }
}

export const apiClient = new ApiClient(serviceBaseUrls.retail);
export const retailApiClient = apiClient;
export const authApiClient = new ApiClient(serviceBaseUrls.auth);
export const sellerApiClient = new ApiClient(serviceBaseUrls.seller);
export const adminApiClient = new ApiClient(serviceBaseUrls.admin);
export const deliveryApiClient = new ApiClient(serviceBaseUrls.delivery);
export const notificationApiClient = new ApiClient(serviceBaseUrls.notifications);
export const documentApiClient = new ApiClient(serviceBaseUrls.documents);
export const workflowApiClient = new ApiClient(serviceBaseUrls.workflow);
export const returnsApiClient = new ApiClient(serviceBaseUrls.returns);

export type { ApiClient };
